import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BodyArrangementsData');

import {
  BodyArrangementsInfoRec,
  bodyArrangementsInfoService,
  TraditionalBurialDetails,
  CremationDetails,
  ScienceDonationDetails,
  GreenBurialDetails,
  MilitaryBurialDetails,
  OtherArrangementDetails,
} from '../../../../services/bodyArrangementsInfo';

type BodyArrangementsDataScreenRouteProp = RouteProp<RootStackParamList, 'BodyArrangementsData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const BodyArrangementsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BodyArrangementsDataScreenRouteProp>();
  const [bodyArrangementsInfo, setBodyArrangementsInfo] = useState<BodyArrangementsInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const bodyArrangementsId = route.params?.bodyArrangementsId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadBodyArrangementsInfo();
  }, []);

  const loadBodyArrangementsInfo = async () => {
    try {
      if (bodyArrangementsId) {
        const info = await bodyArrangementsInfoService.getBodyArrangementsInfoById(bodyArrangementsId, targetUserId);
        setBodyArrangementsInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await bodyArrangementsInfoService.getAllBodyArrangementsInfo(targetUserId);
        if (allInfo.length > 0) {
          setBodyArrangementsInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading body arrangements info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (bodyArrangementsInfo) {
      navigation.navigate('BodyArrangementsInput', {
        bodyArrangementsData: bodyArrangementsInfo,
        isEditing: true
      });
    } else {
      navigation.navigate('BodyArrangementsInput');
    }
  };

  const handleDelete = () => {
    if (bodyArrangementsInfo) {
      Alert.alert(
        'Delete Body Arrangements',
        'Are you sure you want to delete this body arrangements information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await bodyArrangementsInfoService.deleteBodyArrangementsInfo(bodyArrangementsInfo.id);
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting body arrangements info:', error);
                Alert.alert('Error', 'Failed to delete body arrangements information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  const renderArrangementDetails = () => {
    if (!bodyArrangementsInfo || !bodyArrangementsInfo.arrangementDetails) {
      return null;
    }

    const details = bodyArrangementsInfo.arrangementDetails;
    const arrangementType = bodyArrangementsInfo.arrangementType;

    switch (arrangementType) {
      case 'traditional':
        const traditionalDetails = details as TraditionalBurialDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Traditional Burial Details</Text>
            <InfoItem label="Open Casket" value={traditionalDetails.openCasket} />
            <InfoItem label="Burial Clothes" value={traditionalDetails.burialClothes} />
            <InfoItem label="Wear Glasses" value={traditionalDetails.wearGlasses} />
            <InfoItem label="Favorite Hat/Knick-knack" value={traditionalDetails.favoriteHatKnickknack} />
            <InfoItem label="Favorite Shoes" value={traditionalDetails.favoriteShoes} />
          </View>
        );

      case 'cremation':
        const cremationDetails = details as CremationDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Cremation Details</Text>
            <InfoItem label="Body Preparation Method" value={cremationDetails.bodyPreparationMethod} />
            <InfoItem label="Bury in Casket After Cremation" value={cremationDetails.buryInCasketAfterCremation} />
            <InfoItem label="Send Ashes to Space" value={cremationDetails.sendAshesToSpace} />
          </View>
        );

      case 'science':
        const scienceDetails = details as ScienceDonationDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Science Donation Details</Text>
            <InfoItem label="Medical School Preference" value={scienceDetails.medicalSchoolPreference} />
            <InfoItem label="Forensic Research" value={scienceDetails.forensicResearch} />
            <InfoItem label="For-Profit Companies" value={scienceDetails.forProfitCompanies} />
            <InfoItem label="BodyWorld Exhibit" value={scienceDetails.bodyWorldExhibit} />
          </View>
        );

      case 'green':
        const greenDetails = details as GreenBurialDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Green Burial Details</Text>
            <InfoItem label="No Embalming Fluids" value={greenDetails.noEmbalmingFluids} />
            <InfoItem label="Biodegradable Casket" value={greenDetails.biodegradableCasket} />
            <InfoItem label="Natural Grave" value={greenDetails.naturalGrave} />
            <InfoItem label="Mausoleum" value={greenDetails.mausoleum} />
          </View>
        );

      case 'military':
        const militaryDetails = details as MilitaryBurialDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Military Burial Details</Text>
            <InfoItem label="Traditional Military Honors" value={militaryDetails.traditionalMilitaryHonors} />
            <InfoItem label="Flag-Draped Casket" value={militaryDetails.flagDrapedCasket} />
            <InfoItem label="Rifle Volley Salute" value={militaryDetails.rifleVolleySalute} />
            <InfoItem label="Playing of Taps" value={militaryDetails.playingOfTaps} />
            <InfoItem label="Flag Presentation" value={militaryDetails.flagPresentation} />
          </View>
        );

      case 'other':
        const otherDetails = details as OtherArrangementDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Other Arrangement Details</Text>
            <InfoItem label="Rest at Sea" value={otherDetails.restAtSea} />
            <InfoItem label="Liquidify" value={otherDetails.liquidify} />
            <InfoItem label="Crash Test Simulations" value={otherDetails.crashTestSimulations} />
            <InfoItem label="Return to Soil" value={otherDetails.returnToSoil} />
            <InfoItem label="Become a Tree" value={otherDetails.becomeATree} />
            <InfoItem label="Custom Description" value={otherDetails.customDescription} />
          </View>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Body Arrangements Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Body Arrangements Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {bodyArrangementsInfo ? (
            <>
              <InfoItem
                label="Arrangement Type"
                value={bodyArrangementsInfo.arrangementType ?
                  bodyArrangementsInfo.arrangementType.charAt(0).toUpperCase() +
                  bodyArrangementsInfo.arrangementType.slice(1) : 'Not specified'}
              />

              {bodyArrangementsInfo.specificInstructions && (
                <InfoItem label="Specific Instructions" value={bodyArrangementsInfo.specificInstructions} />
              )}

              {bodyArrangementsInfo.preferredLocation && (
                <InfoItem label="Preferred Location" value={bodyArrangementsInfo.preferredLocation} />
              )}

              {bodyArrangementsInfo.contactName && (
                <InfoItem label="Contact Name" value={bodyArrangementsInfo.contactName} />
              )}

              {bodyArrangementsInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={bodyArrangementsInfo.contactPhone} />
              )}

              {bodyArrangementsInfo.estimatedCost && (
                <InfoItem label="Estimated Cost" value={`$${bodyArrangementsInfo.estimatedCost.toLocaleString()}`} />
              )}

              {renderArrangementDetails()}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No body arrangements information found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Body Arrangements</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default BodyArrangementsDataScreen;
